import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Banner } from '../../components/Banner';
import { describeApiError } from '../qrs/errors';
import { uploadLogo } from './binaryApi';
import { contrastRatio, formatRatio, MAX_LOGO_SIZE } from './contrast';
import { LOCAL_PRESETS, presetLabel } from './presets';
import type { EyeShape, GradientType, ModuleShape, QrStyle, StyleIssue } from './types';
import { useStylePreview } from './useStylePreview';
import { BRAND } from '../../brand';

const MODULES: { v: ModuleShape; label: string }[] = [
  { v: 'square', label: 'Cuadrado' },
  { v: 'gapped', label: 'Separado' },
  { v: 'circle', label: 'Círculo' },
  { v: 'rounded', label: 'Redondeado' },
  { v: 'vbars', label: 'Barras verticales' },
  { v: 'hbars', label: 'Barras horizontales' },
];
const EYES: { v: EyeShape; label: string }[] = [
  { v: 'square', label: 'Cuadrado' },
  { v: 'rounded', label: 'Redondeado' },
  { v: 'circle', label: 'Círculo' },
  { v: 'leaf', label: 'Hoja' },
];
const INKS = [
  { v: '#16181D', label: 'Asfalto' },
  { v: '#1B5E20', label: 'Verde oscuro' },
  { v: '#7A1F2B', label: 'Burdeo' },
  { v: '#0B3D91', label: 'Azul' },
  { v: '#F2B90F', label: 'Amarillo' },
];
const GROUNDS = [
  { v: '#FFFFFF', label: 'Blanco' },
  { v: '#FFFDF6', label: 'Crema' },
  { v: '#F4F2EC', label: 'Papel' },
];
const EYE_INKS = [
  { v: '#16181D', label: 'Asfalto' },
  { v: '#2F6B57', label: 'Verde pizarra' },
  { v: '#7A1F2B', label: 'Burdeo' },
];
const GRADIENTS: { v: Exclude<GradientType, 'none'>; label: string }[] = [
  { v: 'radial', label: 'Radial' },
  { v: 'square', label: 'Cuadrado' },
  { v: 'horizontal', label: 'Horizontal' },
  { v: 'vertical', label: 'Vertical' },
];

export interface StyleEditorProps {
  value: QrStyle;
  onChange: (style: QrStyle) => void;
  orgId: string | undefined;
  /** Contenido del QR de la vista previa (por defecto uno de ejemplo). */
  previewData?: string;
  presets?: { id: string; qrStyle: QrStyle }[];
  /** Avisa al contenedor si el estilo tiene problemas bloqueantes (R13.2). */
  onIssues?: (issues: StyleIssue[]) => void;
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id}>
      <div id={id} className="mb-1 text-xs font-medium text-ink-muted">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Option({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-green ${
        selected ? 'border-asphalt bg-yellow-soft font-semibold text-ink' : 'border-line bg-white text-ink hover:bg-mist'
      }`}
    >
      {children}
    </button>
  );
}

function Swatch({ color, label, selected, onClick }: { color: string; label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{ background: color }}
      className={`h-7 w-7 rounded-full border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green ${
        selected ? 'ring-2 ring-asphalt ring-offset-2' : 'border-line'
      }`}
    />
  );
}

export function StyleEditor({ value, onChange, orgId, previewData, presets, onIssues }: StyleEditorProps) {
  const preview = useStylePreview(value, orgId, previewData);
  const serializedIssues = JSON.stringify(preview.issues);
  const onIssuesRef = useRef(onIssues);
  onIssuesRef.current = onIssues;
  useEffect(() => {
    onIssuesRef.current?.(JSON.parse(serializedIssues) as StyleIssue[]);
  }, [serializedIssues]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const ids = { logoSize: useId(), frame: useId(), gradient: useId(), gradOn: useId(), logoOn: useId(), frameOn: useId() };

  const set = (patch: Partial<QrStyle>) => onChange({ ...value, ...patch });
  const setColor = (patch: Partial<QrStyle['color']>) => onChange({ ...value, color: { ...value.color, ...patch } });
  const presetList = presets?.length ? presets : LOCAL_PRESETS;
  const ratio = contrastRatio(value.color.gradient.type === 'none' ? value.color.fg : value.color.gradient.from, value.color.bg);
  const gradientOn = value.color.gradient.type !== 'none';

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploadError(null);
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('El logo pesa más de 2 MB. Sube una versión más liviana.');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadLogo(file, orgId);
      onChange({ ...value, logo: { size: value.logo?.size ?? 0.18, pad: value.logo?.pad ?? 'circle', logoId: res.logoId, url: res.url } });
    } catch (err) {
      setUploadError(describeApiError(err, 'No pudimos subir el logo.'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="grid gap-5 md:grid-cols-5">
      <div className="space-y-4 md:col-span-3">
        <Group label="Presets">
          {presetList.map((p) => (
            <Option key={p.id} selected={false} onClick={() => onChange({ ...p.qrStyle, logo: p.qrStyle.logo ? { ...p.qrStyle.logo, logoId: value.logo?.logoId, url: value.logo?.url } : null })}>
              {presetLabel(p.id)}
            </Option>
          ))}
        </Group>
        <Group label="Forma de los puntos">
          {MODULES.map((m) => (
            <Option key={m.v} selected={value.module === m.v} onClick={() => set({ module: m.v })}>
              {m.label}
            </Option>
          ))}
        </Group>
        <div className="grid gap-4 sm:grid-cols-2">
          <Group label="Marco de las esquinas">
            {EYES.map((e) => (
              <Option key={e.v} selected={value.eyeFrame === e.v} onClick={() => set({ eyeFrame: e.v })}>
                {e.label}
              </Option>
            ))}
          </Group>
          <Group label="Centro de las esquinas">
            {EYES.map((e) => (
              <Option key={e.v} selected={value.eyeBall === e.v} onClick={() => set({ eyeBall: e.v })}>
                {e.label}
              </Option>
            ))}
          </Group>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Group label="Color de los puntos">
            {INKS.map((c) => (
              <Swatch key={c.v} color={c.v} label={c.label} selected={value.color.fg.toUpperCase() === c.v} onClick={() => setColor({ fg: c.v, gradient: { ...value.color.gradient, from: c.v } })} />
            ))}
          </Group>
          <Group label="Fondo (siempre claro)">
            {GROUNDS.map((c) => (
              <Swatch key={c.v} color={c.v} label={c.label} selected={value.color.bg.toUpperCase() === c.v} onClick={() => setColor({ bg: c.v })} />
            ))}
          </Group>
        </div>
        <p className={`text-xs ${ratio < 3 ? 'font-medium text-error-text' : 'text-ink-muted'}`}>
          Contraste {formatRatio(ratio)}
          {ratio < 3 ? ' · bajo el mínimo de 3:1: los celulares pueden no leerlo.' : ' · cumple el mínimo de 3:1.'}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor={ids.gradOn} className="flex items-center gap-2 text-xs font-medium text-ink-muted">
              <input
                id={ids.gradOn}
                type="checkbox"
                checked={gradientOn}
                onChange={(e) => setColor({ gradient: { type: e.target.checked ? 'radial' : 'none', from: value.color.fg, to: value.color.gradient.to === '#000000' ? '#16181D' : value.color.gradient.to } })}
              />
              Degradado en los puntos
            </label>
            {gradientOn && (
              <div className="flex items-center gap-2 text-xs">
                <label htmlFor={ids.gradient} className="sr-only">
                  Tipo de degradado
                </label>
                <select
                  id={ids.gradient}
                  className="rounded-sm border border-line bg-white px-1.5 py-1"
                  value={value.color.gradient.type}
                  onChange={(e) => setColor({ gradient: { ...value.color.gradient, type: e.target.value as GradientType } })}
                >
                  {GRADIENTS.map((g) => (
                    <option key={g.v} value={g.v}>
                      {g.label}
                    </option>
                  ))}
                </select>
                <span>hacia</span>
                {INKS.slice(0, 4).map((c) => (
                  <Swatch key={c.v} color={c.v} label={`Degradado hacia ${c.label}`} selected={value.color.gradient.to.toUpperCase() === c.v} onClick={() => setColor({ gradient: { ...value.color.gradient, to: c.v } })} />
                ))}
              </div>
            )}
          </div>
          <Group label="Color de las esquinas">
            {EYE_INKS.map((c) => (
              <Swatch key={c.v} color={c.v} label={c.label} selected={(value.color.eyeColor ?? '').toUpperCase() === c.v} onClick={() => setColor({ eyeColor: c.v })} />
            ))}
          </Group>
        </div>

        <fieldset className="space-y-2 rounded-lg border border-line p-3">
          <legend className="px-1 text-xs font-medium text-ink-muted">Logo del negocio al centro</legend>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label htmlFor={ids.logoOn} className="flex items-center gap-2 text-xs">
              <input id={ids.logoOn} type="checkbox" checked={Boolean(value.logo)} onChange={(e) => set({ logo: e.target.checked ? { size: 0.18, pad: 'circle' } : null })} />
              Con logo
            </label>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="sr-only" id={`${ids.logoOn}-file`} onChange={(e) => void onFile(e.target.files?.[0])} />
              <label htmlFor={`${ids.logoOn}-file`} className="cursor-pointer rounded-md border border-line bg-white px-2.5 py-1 text-xs font-semibold hover:bg-mist focus-within:outline focus-within:outline-2 focus-within:outline-green">
                {uploading ? 'Subiendo…' : value.logo?.logoId ? 'Cambiar logo' : 'Subir logo (PNG o JPG)'}
              </label>
            </div>
          </div>
          {value.logo && (
            <>
              <div className="flex items-center gap-2 text-xs">
                <label htmlFor={ids.logoSize} className="sr-only">
                  Tamaño del logo
                </label>
                <input
                  id={ids.logoSize}
                  type="range"
                  min={10}
                  max={25}
                  value={Math.round(value.logo.size * 100)}
                  onChange={(e) => set({ logo: { ...value.logo!, size: Number(e.target.value) / 100 } })}
                  className="flex-1 accent-green"
                />
                <span className={`w-12 text-right font-mono tabular-nums ${value.logo.size > MAX_LOGO_SIZE ? 'text-error-text' : ''}`}>{Math.round(value.logo.size * 100)} %</span>
              </div>
              <div className="flex gap-3 text-xs">
                {(['rounded', 'circle'] as const).map((pad) => (
                  <label key={pad} className="flex items-center gap-1">
                    <input type="radio" name={`${ids.logoOn}-pad`} checked={value.logo!.pad === pad} onChange={() => set({ logo: { ...value.logo!, pad } })} />
                    {pad === 'rounded' ? 'Fondo redondeado' : 'Fondo circular'}
                  </label>
                ))}
              </div>
              {!value.logo.logoId && <p className="text-xs text-ink-muted">Sube el logo del negocio para verlo en el QR.</p>}
            </>
          )}
          <p className="text-[11px] text-ink-muted">Máximo 22 % del ancho. El logo es del negocio; la marca {BRAND} nunca va dentro del QR.</p>
          {uploadError && <p role="alert" className="text-xs font-medium text-error-text">{uploadError}</p>}
        </fieldset>

        <div className="grid grid-cols-3 items-end gap-2">
          <div className="col-span-2 flex flex-col gap-1">
            <label htmlFor={ids.frame} className="text-xs font-medium text-ink-muted">
              Marco con llamado (máx. 12)
            </label>
            <input
              id={ids.frame}
              maxLength={12}
              disabled={!value.frame}
              value={value.frame?.text ?? ''}
              onChange={(e) => set({ frame: { text: e.target.value.toUpperCase(), color: value.frame?.color ?? '#16181D' } })}
              className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm disabled:bg-gray-50"
            />
          </div>
          <label htmlFor={ids.frameOn} className="flex items-center gap-2 pb-2 text-xs">
            <input id={ids.frameOn} type="checkbox" checked={Boolean(value.frame)} onChange={(e) => set({ frame: e.target.checked ? { text: 'ESCANEA', color: '#16181D' } : null })} />
            Con marco
          </label>
        </div>
      </div>

      <div className="md:col-span-2">
        <div className="mb-1 text-xs font-medium text-ink-muted">Vista previa en vivo</div>
        <div className="flex aspect-square max-w-full items-center justify-center rounded-lg border border-line bg-white p-3" aria-busy={preview.loading}>
          {preview.imageUrl ? (
            <img src={preview.imageUrl} alt="Vista previa del QR con el estilo elegido" className={`h-full w-full object-contain transition-opacity ${preview.loading ? 'opacity-60' : ''}`} />
          ) : preview.error ? (
            <p className="px-4 text-center text-xs text-ink-muted">{preview.error}</p>
          ) : (
            <div className="h-full w-full animate-pulse rounded bg-gray-100" />
          )}
        </div>
        <div aria-live="polite" className="mt-2 space-y-2">
          {preview.issues.length > 0 ? (
            preview.issues.map((issue, i) => (
              <Banner key={`${issue.code}-${issue.field}-${i}`} tone="warning">
                {issue.message}
              </Banner>
            ))
          ) : (
            <p className="rounded-md bg-gray-50 p-2 text-xs text-gray-700">
              Contraste {formatRatio(ratio)} · fondo más claro que los puntos{value.logo ? ` · logo ${Math.round(value.logo.size * 100)} %` : ''}. Al crear el QR verificamos que se lea con varios lectores.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
